import React, { useEffect, useRef, useState } from "react";
import {
  isSignatureValid,
  normalizeSignature,
  resolveSignatureName,
} from "./signature.utils";

interface FieldRendererProps {
  field: any;
  value: any;
  mode: "edit" | "view";
  onChange?: (value: any) => void;
  user?: any;
  // Sensitive-reveal context — optional. When the field is `sensitive` and
  // all three are supplied, view mode shows a 🔓 button (if canReveal) and
  // wires it to POST /profile-submissions/:userId/:tplId/reveal. Edit mode
  // is unaffected by these props; envelope normalization there runs
  // unconditionally because an envelope object can never be a valid input
  // value regardless of whether the user can decrypt it.
  userId?: string;
  profileTemplateId?: string;
  canReveal?: boolean;
}

const applyMask = (raw: string, mask: string) => {
  let result = "";
  let rawIndex = 0;

  for (let i = 0; i < mask.length; i++) {
    if (mask[i] === "*") {
      if (rawIndex < raw.length) {
        result += raw[rawIndex];
        rawIndex++;
      } else {
        break;
      }
    } else {
      result += mask[i];
    }
  }

  return result;
};

// Mirrors backend `isSensitiveEnvelope` in utils/encrypt.ts. Kept inline (not
// imported from a shared module) because the only signals we need are the
// discriminator + the algorithm tag — both stable contracts. Future envelope
// versions bump `_enc` and the renderer treats unknown versions as still-
// sensitive, falling back to "cannot decrypt here" UX.
function isSensitiveEnvelope(v: unknown): boolean {
  return (
    typeof v === "object" &&
    v !== null &&
    (v as { _enc?: unknown })._enc === 1 &&
    (v as { alg?: unknown }).alg === "aes-256-gcm"
  );
}

const REVEAL_VISIBLE_MS = 60_000;
const MASK_DISPLAY = "********";

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  user,
  userId,
  profileTemplateId,
  canReveal,
}) => {
  // Sensitive-reveal state. All four pieces live together so the cleanup
  // effect can wipe them in one shot when the field unmounts or its value
  // changes (which happens on resubmit + reload).
  const [revealing, setRevealing] = useState(false);
  const [reason, setReason] = useState("");
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [submittingReveal, setSubmittingReveal] = useState(false);
  const remaskTimerRef = useRef<number | null>(null);

  // Clear the auto-remask timer on unmount AND whenever the underlying
  // envelope changes (a resubmit overwrites it on disk; whatever was
  // revealed is now stale and shouldn't keep ticking).
  useEffect(() => {
    return () => {
      if (remaskTimerRef.current !== null) {
        window.clearTimeout(remaskTimerRef.current);
        remaskTimerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    // Reset reveal state whenever the underlying value reference changes.
    // Prevents a stale plaintext from lingering on screen if the parent
    // re-fetches the submission and the envelope rotates.
    if (revealedValue !== null) {
      setRevealedValue(null);
    }
    if (remaskTimerRef.current !== null) {
      window.clearTimeout(remaskTimerRef.current);
      remaskTimerRef.current = null;
    }
  }, [value]);

  const closeRevealModal = () => {
    setRevealing(false);
    setReason("");
    setRevealError(null);
  };

  const performReveal = async () => {
    if (!userId || !profileTemplateId || !reason.trim()) return;
    setSubmittingReveal(true);
    setRevealError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/profile-submissions/${userId}/${profileTemplateId}/reveal`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fieldKey: field.key,
            reason: reason.trim(),
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed (${res.status})`);
      }
      const data = await res.json();
      const plain =
        typeof data?.value === "string" ? data.value : "";
      setRevealedValue(plain);
      closeRevealModal();
      if (remaskTimerRef.current !== null) {
        window.clearTimeout(remaskTimerRef.current);
      }
      remaskTimerRef.current = window.setTimeout(() => {
        setRevealedValue(null);
        remaskTimerRef.current = null;
      }, REVEAL_VISIBLE_MS);
    } catch (err: any) {
      setRevealError(err?.message || "Failed to reveal");
    } finally {
      setSubmittingReveal(false);
    }
  };

  const manualRemask = () => {
    if (remaskTimerRef.current !== null) {
      window.clearTimeout(remaskTimerRef.current);
      remaskTimerRef.current = null;
    }
    setRevealedValue(null);
  };

  // Registration: read-only identity block. Two sources, never user input:
  //   1. Owner pages pass the live session `user` (firstName/lastName/email/
  //      phone) — preferred so the block is always current for the person
  //      filling/viewing their own profile.
  //   2. Admin view passes no `user`; the value is the JSON snapshot the owner
  //      persisted into data[fieldKey] at save time. Stored as a string to
  //      keep the data map primitive-safe; parsed here for display.
  const resolveRegistration = (): {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null => {
    if (user && (user.firstName || user.lastName || user.email || user.name)) {
      return {
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
      };
    }
    let obj: any = null;
    if (typeof value === "string" && value.trim().startsWith("{")) {
      try {
        obj = JSON.parse(value);
      } catch {
        obj = null;
      }
    } else if (value && typeof value === "object") {
      obj = value;
    }
    if (!obj) return null;
    return {
      firstName: obj.firstName || "",
      lastName: obj.lastName || "",
      email: obj.email || "",
      phone: obj.phone || "",
    };
  };

  const renderRegistration = () => {
    const reg = resolveRegistration();
    // First/Last/Email always shown; Phone only when present (per spec).
    const rows: Array<[string, string]> = [
      ["First Name", reg?.firstName || "—"],
      ["Last Name", reg?.lastName || "—"],
      ["Email", reg?.email || "—"],
    ];
    if (reg?.phone) rows.push(["Phone Number", reg.phone]);

    return (
      <div style={{ gridColumn: "1 / -1", width: "100%" }}>
        {field.label && (
          <div
            style={{
              marginTop: "32px",
              marginBottom: "12px",
              fontWeight: 600,
              fontSize: "1.1rem",
              textAlign: "left",
            }}
          >
            {field.label}
          </div>
        )}
        <div className="two-col-grid">
          {rows.map(([label, val]) => (
            <div className="summary-field" key={label}>
              <p className="label">{label}</p>
              <p className="value">{val}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---------- VIEW MODE ----------
  if (mode === "view") {
    if (field.type === "registration") {
      return renderRegistration();
    }

    if (field.type === "heading") {
      return (
        <div
          style={{
            gridColumn: "1 / -1",
            width: "100%",
            marginTop: "32px",
            marginBottom: "12px",
            fontWeight: 600,
            fontSize: "1.1rem",
            textAlign: "left",
          }}
        >
          {field.label}
        </div>
      );
    }

    if (field.type === "checkbox") {
      return (
        <div className="summary-field">
          <p className="label">{field.label}</p>
          <p className="value">{value ? "Confirmed" : "Not confirmed"}</p>
        </div>
      );
    }

    // Sensitive view — applies to all non-heading/checkbox types. Plaintext
    // from a prior reveal call lives in revealedValue and auto-clears after
    // REVEAL_VISIBLE_MS; otherwise the field shows MASK_DISPLAY. Legacy
    // pre-Phase-1 plaintext (`typeof value === "string"` for a sensitive
    // field) is masked the same way — the field is sensitive even if the
    // value isn't yet encrypted.
    if (field.sensitive) {
      const envelope = isSensitiveEnvelope(value);
      const legacyPlain = !envelope && typeof value === "string" && value.length > 0;
      const hasValue = envelope || legacyPlain;
      const display =
        revealedValue !== null
          ? revealedValue
          : hasValue
            ? MASK_DISPLAY
            : "—";
      const showReveal =
        !!canReveal &&
        hasValue &&
        revealedValue === null &&
        !!userId &&
        !!profileTemplateId;
      const showRemask = revealedValue !== null;

      return (
        <div className="summary-field">
          <p className="label">{field.label}</p>
          <p
            className="value"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontFamily: revealedValue !== null ? undefined : "monospace" }}>
              {display}
            </span>
            {showReveal && (
              <button
                type="button"
                onClick={() => {
                  setReason("");
                  setRevealError(null);
                  setRevealing(true);
                }}
                title="Reveal sensitive value (requires reason; access is logged)"
                style={{
                  background: "transparent",
                  border: "1px solid #d4a017",
                  color: "#d4a017",
                  borderRadius: 4,
                  padding: "2px 8px",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                }}
              >
                🔓 Reveal
              </button>
            )}
            {showRemask && (
              <button
                type="button"
                onClick={manualRemask}
                title="Re-mask now"
                style={{
                  background: "transparent",
                  border: "1px solid #9aa0a6",
                  color: "#9aa0a6",
                  borderRadius: 4,
                  padding: "2px 8px",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                }}
              >
                🔒 Hide
              </button>
            )}
          </p>

          {revealing && (
            <div
              className="smrtdocs-modal-overlay"
              onClick={(e) => {
                if (e.target === e.currentTarget && !submittingReveal) {
                  closeRevealModal();
                }
              }}
            >
              <div
                className="smrtdocs-modal"
                style={{ width: "480px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="smrtdocs-modal-header">
                  <h2 style={{ margin: 0, fontSize: "1rem" }}>
                    Reveal Sensitive Field
                  </h2>
                </div>
                <div className="smrtdocs-modal-body">
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "#9aa0a6",
                      lineHeight: 1.5,
                      margin: "0 0 12px",
                    }}
                  >
                    You are about to view <strong>{field.label}</strong>. This
                    action is logged with your user, the field, your reason,
                    and the time. The value will be hidden again after 60
                    seconds.
                  </p>
                  <div
                    style={{
                      marginBottom: 6,
                      fontSize: "0.75rem",
                      color: "#9aa0a6",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Reason for Access *
                  </div>
                  <textarea
                    className="manage-input"
                    placeholder="e.g. payroll setup for new hire"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{
                      width: "100%",
                      height: 80,
                      padding: "8px 10px",
                      resize: "vertical",
                      boxSizing: "border-box",
                    }}
                    disabled={submittingReveal}
                  />
                  {revealError && (
                    <p
                      style={{
                        fontSize: "0.8rem",
                        color: "#ef9a9a",
                        background: "rgba(229, 57, 53, 0.12)",
                        padding: "6px 10px",
                        borderRadius: 4,
                        margin: "10px 0 0",
                      }}
                    >
                      {revealError}
                    </p>
                  )}
                </div>
                <div
                  style={{
                    padding: "12px 20px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    borderTop: "1px solid #555",
                  }}
                >
                  <button
                    className="btn-cancel"
                    onClick={closeRevealModal}
                    disabled={submittingReveal}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-save"
                    style={{
                      background: "#d4a017",
                      borderColor: "#d4a017",
                      opacity: submittingReveal || !reason.trim() ? 0.6 : 1,
                      cursor:
                        submittingReveal || !reason.trim()
                          ? "not-allowed"
                          : "pointer",
                    }}
                    disabled={submittingReveal || !reason.trim()}
                    onClick={performReveal}
                  >
                    {submittingReveal ? "Revealing…" : "Reveal"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="summary-field">
        <p className="label">{field.label}</p>
        <p className="value">
          {value
            ? field.mask
              ? applyMask(value.toString(), field.mask)
              : value.toString()
            : "—"}
        </p>
      </div>
    );
  }

  // ---------- EDIT MODE ----------

  // Envelope guard: when admin reopens a profile, the previous submission's
  // encrypted values come back as envelope objects. The edit-mode branches
  // below assume `value` is a scalar — feeding an object into <input value>
  // would render "[object Object]". Normalize to empty string so the user
  // re-enters the sensitive value, which the submit handler will re-encrypt.
  // Non-envelope values pass through unchanged.
  if (isSensitiveEnvelope(value)) {
    value = "";
  }

  // Registration is read-only in edit mode too — same block as view, no input.
  if (field.type === "registration") {
    return renderRegistration();
  }

  if (field.type === "heading") {
    return (
      <div
        style={{
          gridColumn: "1 / -1",
          width: "100%",
          marginTop: "32px",
          marginBottom: "12px",
          fontWeight: 600,
          fontSize: "1.1rem",
          textAlign: "left",
        }}
      >
        {field.label}
      </div>
    );
  }

  if (field.type === "certification") {
    const fullName = resolveSignatureName(user);
    const sig = normalizeSignature(value);
    const sigMatch = isSignatureValid(value, fullName);

    return (
      <div className="certification-block" style={{ background: "#1a1a1a", border: `1px solid ${sigMatch ? "#1faa59" : "#2a2a2a"}`, borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
        {field.helper && (
          <div className="certification-text" style={{ fontSize: "0.95rem", color: "#fff", marginBottom: 6 }}>{field.helper}</div>
        )}

        <input
          type="text"
          className="certification-input"
          placeholder={
            fullName ? `Type "${fullName}" to sign` : "Type your full name to sign"
          }
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange?.(e.target.value)}
          // Mobile keyboards otherwise autocapitalise and autocorrect the name
          // mid-entry, which reads as the field fighting the user even though
          // the match itself now tolerates it.
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          style={{ borderColor: sig.length > 0 ? (sigMatch ? "#1faa59" : "#c62828") : undefined }}
        />
        {sig.length > 0 && !sigMatch && (
          <div style={{ fontSize: "0.72rem", color: "#c62828", marginTop: 4 }}>
            Signature must match: {fullName}
          </div>
        )}
        {sigMatch && (
          <div style={{ fontSize: "0.72rem", color: "#1faa59", marginTop: 2 }}>
            Signed
          </div>
        )}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div className="field-wrap">
        <div className="checkbox-block">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange?.(e.target.checked)}
            />
            <span className="checkbox-label">{field.label}</span>
          </label>

          {field.helper && (
            <div className="checkbox-helper">{field.helper}</div>
          )}
        </div>
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className="field-wrap">
        <select
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
        >
          <option value="" disabled>
            {field.helper || field.label}
          </option>
          {field.options?.map((opt: string) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "date") {
    return (
      <div className="field-wrap date-wrapper">
        {!value && (
          <span className="date-placeholder">
            {field.helper || field.label}
          </span>
        )}
        <input
          type="date"
          className={!value ? "date-empty" : ""}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div className="field-wrap">
        <input
          type="text"
          inputMode="numeric"
          placeholder={field.helper || field.label}
          value={field.mask ? applyMask(value || "", field.mask) : value || ""}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "");

            if (
              field.maxLength !== undefined &&
              digits.length > field.maxLength
            ) {
              return;
            }

            onChange?.(digits);
          }}
        />
      </div>
    );
  }

  // Default text input
  return (
    <div className="field-wrap">
      <input
        type="text"
        placeholder={field.helper || field.label}
        value={field.mask ? applyMask(value || "", field.mask) : value || ""}
        onChange={(e) => {
          let raw = e.target.value;

          if (field.mask) {
            // Strip spaces and non-alphanumeric
            raw = raw.replace(/\s/g, "");
            raw = raw.replace(/[^a-zA-Z0-9]/g, "");

            const maskLength = (field.mask.match(/\*/g) || []).length;

            if (raw.length > maskLength) {
              return;
            }
          } else {
            if (field.maxLength !== undefined && raw.length > field.maxLength) {
              return;
            }
          }

          onChange?.(raw);
        }}
      />
    </div>
  );
};

export default FieldRenderer;
